import * as React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { Link } from "gatsby-theme-material-ui";
import { kebabCase } from "lodash";

import Box from '@material-ui/core/Box';
import { blue } from '@material-ui/core/colors';
import Grid from '@material-ui/core/Grid';
import TreeItem from '@material-ui/lab/TreeItem';
import TreeView from '@material-ui/lab/TreeView';
import Typography from "@material-ui/core/Typography";

import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import BlogChip from './BlogChip';

const formatMonth = {
  '01': 'January',
  '02': 'February',
  '03': 'March',
  '04': 'April',
  '05': 'May',
  '06': 'June',
  '07': 'July',
  '08': 'August',
  '09': 'September',
  '10': 'October',
  '11': 'November',
  '12': 'December',
};

export const useBrewlogMetadata = () => {
  const data = useStaticQuery(
    graphql`
      query {
        brewlog: allMdx {
          tags: group(field: frontmatter___tags) {
            name: fieldValue,
            count: totalCount
          }
          months: group(field: fields___year_month) {
            year_month: fieldValue
            count: totalCount
            nodes {
              id
              slug
              frontmatter {
                title
              }
              fields {
                month
              }
            }
          }
        }
        cellared: allMdx(
          filter: {
            frontmatter: { tags: { in: "cellared" }}
          }
        ) {
          edges {
            node {
              id
              slug
              frontmatter {
                title
              }
            }
          }
        }
        brewing: allMdx(
          filter: {
            frontmatter: { tags: { in: "brewing" }}
          }
        ) {
          edges {
            node {
              id
              slug
              frontmatter {
                title
              }
            }
          }
        }
      }
    `
  );
  const years = [
    ...new Set(data.brewlog.months.map(group => group.year_month.substr(0, 4)))
  ].sort();
  const months = [
    ...new Set(data.brewlog.months.map(group => group.year_month.substr(5, 2)))
  ].sort();
  const dates = years.map(year => ({
    value: year,
    months: months.map(
      month => ({
        value: month,
        articles: data.brewlog.months.filter(group =>
          group.year_month.startsWith(year) && group.year_month.endsWith(month)
        ).map(group => group.nodes)
      })
    )
  }));

  return {
    tags: data.brewlog.tags,
    cellared: data.cellared.edges.map(edge => { return { ...edge.node } }),
    brewing: data.brewing.edges.map(edge => { return { ...edge.node } }),
    dates: dates
  };
}

export default function Brewlog({ children }) {
  const { tags, cellared, brewing, dates } = useBrewlogMetadata();
  return (
    <Grid container>
      <Grid item xs={12} md={9}>
        {children}
      </Grid>
      <Grid item xs={12} md={3}>
        <Typography component="h5" variant="h6">Brewing</Typography>
        <ArticleChips articles={brewing} />
        <Typography component="h5" variant="h6" mt={2}>Cellared</Typography>
        <ArticleChips articles={cellared} />
        <Typography component="h5" variant="h6" mt={2}>Articles</Typography>
        <TagList tags={tags.sort((a, b) => a.name.localeCompare(b.name))} all={true} />
        <ArticleDateTree dates={dates} />
      </Grid>
    </Grid >
  );
}

export function ArticleChips({ articles }) {
  return (
    articles.length
      ? <Box>
        {articles.map(article =>
          <BlogChip to={`/${article.slug}`} label={article.frontmatter.title}
            key={article.slug} />
        )}
      </Box>
      : <Typography paragraph mb={0}>None</Typography>
  );
}

export function TagList({ tags, children, all }) {
  return (
    <Box mt={1}>
      {
        all && <BlogChip to="/brewlog/articles" label='All' color="secondary" />
      }
      {(tags.length &&
        tags.map(tag => (
          <BlogChip
            to={`/brewlog/tags/${kebabCase(tag.name)}`}
            label={tag.name}
            key={tag.name} />
        ))
      )}
      {children}
    </Box>
  );
}

function ArticleDateTree({ dates }) {
  return (
    <TreeView
      aria-label="articles-by-month"
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpandIcon={<ChevronRightIcon />}
      sx={{ flexGrow: 1, maxWidth: 400, overflowY: 'auto', marginTop: 2 }}
    >
      {dates.map(year =>
        <TreeItem nodeId={year.value} label={year.value} key={year.value}>
          {year.months.map(month =>
            <TreeItem
              nodeId={year.value + month.value}
              label={formatMonth[month.value]}
              key={month.value}>
              {month.articles.map((article, index) =>
                <TreeItem
                  nodeId={year.value + month.value + index}
                  label={<Link to={`/${article[0].slug}`}>{article[0].frontmatter.title}</Link>}
                  key={year.value + month.value + index}
                />
              )}
            </TreeItem>
          )}
        </TreeItem>
      )}
    </TreeView>
  )
}